'use client';

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { V2Layout, RevealSection, useV2Lang, Eyebrow, Rule, ACCENT, HAIRLINE, formatDate, venueShort, PageHeader } from '../_layout';
import { appLanguageToContentLocale } from '@/i18n/contentLocale';
import { eventHref } from '../eventSlugs';

type Slot = { start_time: string; end_time: string; location?: string; isExpired?: boolean };
type Activity = { id: number; type: string; title: string; location: string; tag?: string; month?: string | number; slots: Slot[] };

const COPY = {
  CN: {
    kicker: '档案',
    title: '所有活动',
    lede: '我们做过、正在做、即将做的全部活动。可按类型、状态、年份多维度筛选；点进任意一项查看完整故事。',
    dim: { type: '类型', timing: '状态', year: '年份' },
    chips: {
      all: '全部', music: '音乐', poetry: '诗歌', workshop: '工作坊', other: '其他',
      upcoming: '即将到来', ongoing: '正在进行', past: '已结束',
    },
    loading: '加载中…', empty: '没有匹配的活动',
    readMore: '阅读 ↗', clear: '清除',
    countShown: '显示', countOf: '共',
  },
  EN: {
    kicker: 'Archive',
    title: 'All events',
    lede: "Everything we've done, are doing, and will do. Filter by type, timing or year. Click any item for the full story.",
    dim: { type: 'Type', timing: 'Timing', year: 'Year' },
    chips: {
      all: 'All', music: 'Music', poetry: 'Poetry', workshop: 'Workshop', other: 'Other',
      upcoming: 'Upcoming', ongoing: 'Ongoing', past: 'Past',
    },
    loading: 'Loading…', empty: 'No matching events',
    readMore: 'Read ↗', clear: 'Clear',
    countShown: 'Showing', countOf: 'of',
  },
  DE: {
    kicker: 'Archiv',
    title: 'Alle Events',
    lede: 'Alles, was wir gemacht haben, machen oder vorhaben. Nach Typ, Zeitpunkt oder Jahr filtern.',
    dim: { type: 'Typ', timing: 'Zeitpunkt', year: 'Jahr' },
    chips: {
      all: 'Alle', music: 'Musik', poetry: 'Poesie', workshop: 'Workshop', other: 'Sonstige',
      upcoming: 'Kommend', ongoing: 'Laufend', past: 'Vergangen',
    },
    loading: 'Lädt…', empty: 'Keine passenden Events',
    readMore: 'Lesen ↗', clear: 'Zurücksetzen',
    countShown: 'Anzeige',  countOf: 'von',
  },
};

type TypeKey = 'all' | 'music' | 'poetry' | 'workshop' | 'other';
type TimingKey = 'all' | 'upcoming' | 'ongoing' | 'past';

function classifyType(ev: Activity): Exclude<TypeKey, 'all'> {
  const t = (ev.type || '').toLowerCase();
  const tag = (ev.tag || '').toLowerCase();
  if (t === 'music' || tag === 'music') return 'music';
  if (t === 'poetry' || tag === 'poetry') return 'poetry';
  if (t.includes('workshop') || t.includes('工作坊') || tag.includes('workshop')) return 'workshop';
  return 'other';
}

function classifyTiming(ev: Activity, now: number): Exclude<TimingKey, 'all'> {
  if (!ev.slots || ev.slots.length === 0) return 'past';
  const start = new Date(ev.slots[0].start_time.replace(' ', 'T')).getTime();
  const end = new Date(ev.slots[ev.slots.length - 1].end_time.replace(' ', 'T')).getTime();
  if (start > now) return 'upcoming';
  if (end < now) return 'past';
  return 'ongoing';
}

export default function V2EventsList() {
  const lang = useV2Lang();
  const t = COPY[lang];
  const [events, setEvents] = useState<Activity[]>([]);
  const [type, setType] = useState<TypeKey>('all');
  const [timing, setTiming] = useState<TimingKey>('all');
  const [year, setYear] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const apiLang = appLanguageToContentLocale(lang);
    fetch(`/api/activities?language=${apiLang}&pageSize=200`)
      .then((r) => r.json())
      .then((data) => setEvents(Array.isArray(data) ? data : data?.activities ?? data?.data ?? []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [lang]);

  const now = Date.now();

  // Available years from data — sorted descending
  const availableYears = useMemo(() => {
    const set = new Set<string>();
    for (const ev of events) {
      if (!ev.slots || ev.slots.length === 0) continue;
      const y = new Date(ev.slots[0].start_time.replace(' ', 'T')).getFullYear();
      if (!Number.isNaN(y)) set.add(String(y));
    }
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [events]);

  const filtered = useMemo(() => {
    return events
      .filter((e) => e.slots && e.slots.length > 0)
      .filter((e) => (type === 'all' ? true : classifyType(e) === type))
      .filter((e) => (timing === 'all' ? true : classifyTiming(e, now) === timing))
      .filter((e) => {
        if (year === 'all') return true;
        const y = String(new Date(e.slots[0].start_time.replace(' ', 'T')).getFullYear());
        return y === year;
      })
      .sort(
        (a, b) =>
          new Date(b.slots[0].start_time.replace(' ', 'T')).getTime() -
          new Date(a.slots[0].start_time.replace(' ', 'T')).getTime()
      );
  }, [events, type, timing, year, now]);

  // Group by year (only when year filter is "all")
  const grouped = useMemo(() => {
    const m: Record<string, Activity[]> = {};
    for (const ev of filtered) {
      const y = String(new Date(ev.slots[0].start_time.replace(' ', 'T')).getFullYear());
      if (!m[y]) m[y] = [];
      m[y].push(ev);
    }
    return m;
  }, [filtered]);

  const groupYears = Object.keys(grouped).sort((a, b) => Number(b) - Number(a));

  const isFiltered = type !== 'all' || timing !== 'all' || year !== 'all';

  return (
    <V2Layout>
      <PageHeader kicker={t.kicker} title={t.title} lede={t.lede} />

      {/* Multi-dim filters */}
      <RevealSection className="px-6 md:px-10">
        <div className="mx-auto max-w-[1400px]">
          <div className="border-t" style={{ borderColor: HAIRLINE }}>
            <FilterRow label={t.dim.type}>
              <Chip active={type === 'all'} onClick={() => setType('all')}>{t.chips.all}</Chip>
              <Chip active={type === 'music'} onClick={() => setType('music')}>{t.chips.music}</Chip>
              <Chip active={type === 'poetry'} onClick={() => setType('poetry')}>{t.chips.poetry}</Chip>
              <Chip active={type === 'workshop'} onClick={() => setType('workshop')}>{t.chips.workshop}</Chip>
              <Chip active={type === 'other'} onClick={() => setType('other')}>{t.chips.other}</Chip>
            </FilterRow>

            <FilterRow label={t.dim.timing}>
              <Chip active={timing === 'all'} onClick={() => setTiming('all')}>{t.chips.all}</Chip>
              <Chip active={timing === 'upcoming'} onClick={() => setTiming('upcoming')}>{t.chips.upcoming}</Chip>
              <Chip active={timing === 'ongoing'} onClick={() => setTiming('ongoing')}>{t.chips.ongoing}</Chip>
              <Chip active={timing === 'past'} onClick={() => setTiming('past')}>{t.chips.past}</Chip>
            </FilterRow>

            <FilterRow label={t.dim.year}>
              <Chip active={year === 'all'} onClick={() => setYear('all')}>{t.chips.all}</Chip>
              {availableYears.map((y) => (
                <Chip key={y} active={year === y} onClick={() => setYear(y)}>{y}</Chip>
              ))}
            </FilterRow>
          </div>

          <div className="flex items-center justify-between mt-6 mb-2 text-[12px] font-mono uppercase tracking-[0.15em] text-white/55">
            <span>
              {t.countShown} <span className="text-white font-semibold">{filtered.length}</span> {t.countOf}{' '}
              <span className="text-white/70">{events.length}</span>
            </span>
            {isFiltered && (
              <button
                onClick={() => {
                  setType('all'); setTiming('all'); setYear('all');
                }}
                className="text-white/55 hover:text-white border-b-4 pb-2 font-semibold tracking-wide"
                style={{ borderColor: ACCENT, color: ACCENT }}
              >
                {t.clear} ✕
              </button>
            )}
          </div>
        </div>
      </RevealSection>

      {/* List grouped by year */}
      <RevealSection className="px-6 md:px-10 pb-32">
        <div className="mx-auto max-w-[1400px]">
          {loading && <div className="py-24 text-center text-white/55 text-sm">{t.loading}</div>}
          {!loading && filtered.length === 0 && (
            <div className="py-24 text-center text-white/55 text-sm">{t.empty}</div>
          )}
          {groupYears.map((y) => (
            <div key={y} className="mt-8">
              <h2
                className="sticky top-16 bg-[#0a0a0a]/90 backdrop-blur-sm py-3 -mx-6 md:-mx-10 px-6 md:px-10 z-10 text-[clamp(48px,7vw,96px)] leading-none font-black tracking-[-0.045em] text-white/15 select-none"
              >
                {y}
              </h2>
              <div className="border-t" style={{ borderColor: HAIRLINE }}>
                {grouped[y].map((ev) => {
                  const f = formatDate(ev.slots[0].start_time, lang);
                  const tk = classifyType(ev);
                  const tm = classifyTiming(ev, now);
                  const isPast = tm === 'past';
                  return (
                    <Link
                      key={ev.id}
                      to={eventHref(ev.id)}
                      className="grid grid-cols-12 gap-4 py-7 md:py-9 border-b items-baseline group hover:bg-white/[0.02] transition-colors px-1"
                      style={{ borderColor: HAIRLINE }}
                    >
                      <span className="col-span-3 md:col-span-2 font-mono text-[13px] uppercase tracking-wider text-white">
                        {f.month} {f.day}
                      </span>
                      <span
                        className="col-span-2 md:col-span-1 font-mono text-[10px] uppercase tracking-[0.15em] font-semibold"
                        style={{ color: isPast ? 'rgba(255,255,255,0.45)' : ACCENT }}
                      >
                        {tk === 'music' ? '♪' : tk === 'poetry' ? '✎' : tk === 'workshop' ? '⌘' : '·'}{' '}
                        {t.chips[tk] ?? tk}
                      </span>
                      <h4 className="v2-cjk-tight col-span-12 md:col-span-6 text-[clamp(20px,1.8vw,28px)] leading-[1.25] tracking-[-0.015em] font-semibold text-white">
                        {ev.title}
                      </h4>
                      <span className="col-span-7 md:col-span-2 text-[13px] text-white/65">
                        {venueShort(ev.slots[0].location ?? ev.location)}
                      </span>
                      <span className="col-span-5 md:col-span-1 text-right text-[12px] font-mono uppercase tracking-wider text-white/55 group-hover:text-white">
                        {t.readMore}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </RevealSection>

      <Rule />
    </V2Layout>
  );
}

/* ──────── Filter atoms ──────── */

function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      className="grid grid-cols-12 gap-4 py-5 border-b items-center"
      style={{ borderColor: HAIRLINE }}
    >
      <div className="col-span-12 md:col-span-2">
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/55 font-semibold">
          {label}
        </span>
      </div>
      <div className="col-span-12 md:col-span-10 flex flex-wrap gap-x-1 gap-y-2">
        {children}
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-[12px] font-mono uppercase tracking-[0.15em] transition-colors border ${
        active
          ? 'text-white font-semibold'
          : 'text-white/55 hover:text-white border-transparent font-medium'
      }`}
      style={
        active
          ? { borderColor: ACCENT, color: ACCENT, backgroundColor: 'rgba(255,69,58,0.08)' }
          : { borderColor: 'transparent' }
      }
    >
      {children}
    </button>
  );
}
